import { record, upper, type Row } from '../ability/context';
import { readItemField } from './item-field-reader';
import { readInventoryField, readItemUuidSlot } from './inventory-raw-readers';
import { statusItemId, statusRows } from '../statuses/expiry';
import { entityRuntimeMetadata } from './entity-runtime-metadata';
import { constructPlayerPrefixFromBase } from './from-base-player-prefix';
import { constructPlayerTierSkillsFromBase } from './from-base-player-skills';
import { constructPlayerInventoryGrantsFromBase, createFromBasePlayerStageContext, constructPlayerStatusEnvironmentFromBase } from './from-base-player-stages';
import { constructPlayerNestedGrantsFromBase, createFromBasePlayerGrantContext } from './from-base-player-grants';
import { constructPlayerIntegersFromBase } from './from-base-player-integers';
import { constructPlayerForcedSkillsFromBase } from './from-base-player-forced-skills';
import { constructPlayerFullSkillEntriesFromBase } from './from-base-player-full-skills';
import { constructPlayerFeaturesFromBase } from './from-base-player-features';
import { constructPlayerDefenseSeedsFromBase, constructPlayerDefenseGrantsFromBase } from './from-base-player-defenses';
import { constructPlayerArmorClassFromBase } from './from-base-player-ac';
import { constructPlayerResourcesFromBase } from './from-base-player-resources';
import { constructActorAbilitiesFromBase } from './from-base-player-abilities';
import { createCreatureSizeRecalculator } from './creature-size-recalculation';
import { relocateCreatureDerivedToComputed } from './creature-derived-relocation';
import { statusProjectionDependencies } from './status-attack-budget';
import { createFromBasePlayerContext, withFromBaseValidation, type FromBasePlayerInput } from './from-base-context';
import type { MemberRecompileResult, MemberStatusStep } from './member-status-recompile';
import { formInventoryWrites } from '../statuses/shapeshift-inventory';
import { createStatusLayerReadContext } from './status-row-construction';
import { createInventoryTotalsCompiler } from './inventory-totals';
import { createCreatureEquippedCountsCompiler } from './creature-equipped-counts';
import { SHAPESHIFT_REMOVE_LAYERS } from './shapeshift-layer-lists';
import { constructPlayerClustersFromBase } from '../port/k1-compile/from-base/player-clusters';
import { createAbilityConditionEvaluator } from './ability-conditions';
import { compileStatusRootFields } from './status-root-residue';
import { normalizeFromBaseCatalog } from './from-base-catalog-normalization';
import { createVitalityCompiler } from './vitality-build-context';
import { countStatusCompileWork } from './member-status-diff';
import { constructFromBaseReachWatch } from './from-base-reach-watch';
import { createInventoryTemplateIdentityResolver } from './inventory-template-identity';
import { constructPlayerPostFinalizerFromBase } from './from-base-player-post';
import { compileCreatureMainWeaponDamageType } from './creature-main-weapon-damage-type';
import { readEncumbranceApplications } from './encumbrance-application-plans';
import { readStatusCasterId } from './creature-status-reader';
import { readAbilityEntryId } from './inventory-ability-grant-helpers';
import { constructPlayerAbilityClustersFromBase, constructPlayerSpellClustersFromBase, normalizePlayerClusterValues } from './from-base-player-normalization';
import { createPlayerDefinitionFinalizer } from './from-base-player-sections';
import { createCreatureShapeshiftCompiler } from './creature-shapeshift';
import { c1ProvisionalStatusChainId } from './possessor-compile';
import rowContract from '../../../catalogs/janus/row-contract.json';
import { createFromBaseNpcContext, createFromBaseNpcGrantContext } from './from-base-npc-stages';
import { createNpcDefinitionFinalizer } from './from-base-npc-finalization';
import { constructNpcFormRemovalWindow } from './form-revert-npc';
import { mergeInventoryMetadata } from './inventory-shared-catalog';
import { CPI_FAMILY_OFFSET } from './passive-integer-fold';
import { phpJsonInt, phpJsonTruthy } from './php-json-values';

// These are the form graft's outputs and the equipment-dependent clusters. Saved
// resources, prepared spells, media, catalogs and unrelated cache stay held.
const CLUSTERS = ['size', 'essences', 'skills', 'skill_tier_grants', 'defenses', 'ac', 'damage_mitigation', 'capacity',
    'meters', 'modifiers', 'modifier_sources', 'combat', 'abilities', 'castable', 'spells_known', 'equipped', 'weight',
    'senses', 'speeds', 'attacks', 'reach', 'reach_watch', 'natural_weapon', 'natural_armor', 'skill_ability',
    'suppressed_ability_ids', 'overrun_charges_remaining'] as const;
const FORM_KEYS = ['shapeshift', 'shapeshift_pic', 'shapeshift_charge_mods'] as const;

/** Diff one owned normalized cluster against its held rows. Equal buckets and
 * equal rows (also across insertions/removals) keep their held identity. */
function diffFormCluster(before: unknown, next: unknown): unknown {
    if (Object.is(before, next)) return before;
    if (Array.isArray(before) && Array.isArray(next)) {
        const held = new Map(before.map(entry => [JSON.stringify(entry), entry]));
        let equal = before.length === next.length;
        for (let i = 0; i < next.length; i++) {
            const signature = JSON.stringify(next[i]);
            next[i] = held.has(signature) ? held.get(signature) : diffFormCluster(before[i], next[i]);
            if (next[i] !== before[i]) equal = false;
        }
        return equal ? before : next;
    }
    const prior = record(before), candidate = record(next);
    if (!prior || !candidate) return next;
    const keys = Object.keys(candidate);
    let equal = Object.keys(prior).length === keys.length;
    for (const key of keys) {
        candidate[key] = diffFormCluster(prior[key], candidate[key]);
        if (!Object.prototype.hasOwnProperty.call(prior, key) || candidate[key] !== prior[key]) equal = false;
    }
    return equal ? before : next;
}

/** Reconstruct a form-owned source/cluster window, including initial NPC absorption, never a complete creature.
 * No entry compiler, inventory compiler, status dispatch, save or network read.
 * Missing source coverage is returned to the caller's existing recovery path. */
export function recompileFormRemoval(row: Row, steps: readonly MemberStatusStep[], definition: (id: string) => Row | null): MemberRecompileResult | null {
    const pageDefinition = definition, heldStatuses = statusRows(row, 'target');
    definition = id => pageDefinition(id) ?? record(heldStatuses.find(status => statusItemId(status) === upper(id))?.status_arr);
    const forms = steps.filter(step => step.kind === 'remove' && readItemField(definition(upper(step.statusItemId)), 'item_type') === 'creature');
    const additions = steps.filter(step => step.kind === 'add');
    const replacement = additions.filter(step => readItemField(definition(upper(step.statusItemId)), 'item_type') === 'creature');
    const initialNpc = !record(row.player_characters) && !forms.length && replacement.length === 1;
    if (!forms.length && !initialNpc) return null;
    const fail = (reason: string): MemberRecompileResult => ({ ok: false, reason: 'form-revert:' + reason });
    if (additions.length && replacement.length !== 1) return fail('replacement-form-not-held');
    const removed = new Set(steps.flatMap(step => step.kind === 'remove' ? [upper(step.statusId)] : []));
    const target = statusRows(row, 'target').filter(status => !removed.has(upper(readItemField(status, 'status_id'))));
    const survivingForm = target.some(status => readItemField(definition(statusItemId(status)), 'item_type') === 'creature');
    const formRemains = survivingForm || replacement.length > 0;
    const bridge = (globalThis as { ftFormRevertSource?: (id: string) => { base: Row; metadata: Row } | undefined }).ftFormRevertSource;
    const local = record(row._entity_base);
    const envelope = record(local?.metadata) ? local : bridge?.(upper(readItemField(row.encounter_creatures as Row, 'encounter_creature_id')));
    const rawMetadata = record(envelope?.metadata);
    if (!rawMetadata) return fail('sources-not-held');
    const player = !!record(row.player_characters);
    if (!player && readItemField(row.encounter_creatures as Row, 'item_type') !== 'creature') return fail('non-creature-source-not-ported');
    if (!player && replacement.length && !initialNpc) return fail('npc-replacement-not-ported');
    const computed = record(row.computed), prior = record(computed?.creature), vitality = record(computed?.vitality);
    if (!prior || !vitality) return fail('clusters-not-held');
    const targetId = upper(readItemField(row.encounter_creatures as Row, 'encounter_creature_id'));
    // Trial additions use the same saved-column contract as an applied frame;
    // lean status omissions are not raw source-column omissions.
    const added = additions.map((step, index) => ({ ...Object.fromEntries(rowContract.statuses.columns.map(key => [key, null])),
        status_id: upper(step.statusId) || c1ProvisionalStatusChainId(index), status_private: 0,
        status_item_id: upper(step.statusItemId), status_target_id: targetId, status_caster_id: upper(step.casterId) || targetId,
        status_stack: step.stack ?? 1 }));
    return withFromBaseValidation(() => { try {
        const meta = { ...entityRuntimeMetadata(rawMetadata as Parameters<typeof entityRuntimeMetadata>[0]).player,
            definitionLookup: definition };
        // Removing a form restores feat/passive buckets as well as body/gear.
        // Their integer contributions can feed any modifier in this cluster.
        const changedLayers = new Set<string>([...SHAPESHIFT_REMOVE_LAYERS, ...meta.rollup.integerLayers]);
        const read = createStatusLayerReadContext(statusProjectionDependencies(row, definition));
        const inventoryWrites = survivingForm ? [] : formInventoryWrites(row, Array.isArray(row.inventory) ? row.inventory : [], readInventoryField, read.layerValue);
        // Source-only input: never pass a compiled member/catalog to a raw stage.
        const rawBase = record(envelope?.base);
        if (!player) meta.inventoryMetadata = mergeInventoryMetadata(meta.inventoryMetadata!, rawBase?.inventory_metadata);
        const sourceChoices = rawBase?.user_choices, sourcePictures = rawBase?.pictures;
        const base: FromBasePlayerInput = { ...rawBase as unknown as FromBasePlayerInput,
            entity_id: upper(readItemField(row.encounter_creatures as Row, 'encounter_creature_id')),
            base_rev: 0, ...(player ? { player_characters: record(rawBase?.player_characters) ?? row.player_characters as Row } : {}),
            catalog: record(record(envelope?.base)?.catalog) as Record<string, Row> ?? {},
            user_choices: Array.isArray(sourceChoices) ? sourceChoices : Array.isArray(row.user_choices) ? row.user_choices : [],
            pictures: Array.isArray(sourcePictures) ? sourcePictures : [],
            inventory: (Array.isArray(row.inventory) ? row.inventory : []).map((entry: Row) => ({ inventory: record(entry.inventory) ?? {} })),
            charges: Array.isArray(record(envelope?.base)?.charges) ? record(envelope?.base)!.charges as Row[] : [],
            encounter_creatures: row.encounter_creatures as Row };
        const encounterContext = record(rawBase?.encounter_context);
        if (encounterContext) base.encounter_context = encounterContext;
        const projected = new Set(readEncumbranceApplications(row).map(plan => upper(plan.status_id)));
        base.statuses = Object.fromEntries((['target', 'caster'] as const).map(mode => [mode, [...statusRows(row, mode)
            .filter(status => !removed.has(upper(readItemField(status, 'status_id'))) && !projected.has(upper(readItemField(status, 'status_id'))))
            .map(status => {
                const saved = record(rawBase?.statuses)?.[mode];
                const raw = (Array.isArray(saved) ? saved as Row[] : []).find(raw =>
                    upper(readItemField(raw, 'status_id')) === upper(readItemField(status, 'status_id')));
                // Raw rows carry the zero/null columns omitted by the held
                // compiled status. Only saved columns enter the raw stage.
                if (!raw) throw new Error('status-source-not-held');
                return Object.fromEntries(Object.keys(raw).map(key => [key,
                    key === 'status_item_id' ? statusItemId(status) || readItemUuidSlot(readItemField(raw, key))
                        : key === 'status_caster_id' ? readStatusCasterId(status) : readItemField(status, key)]));
            }), ...added.filter(status => mode === 'target' || status.status_caster_id === targetId)]]));
        // Catalog normalization can leave name-only feat/essence shells. They
        // are display identities, not sources for the affected rule clusters.
        const raw = player ? createFromBasePlayerContext(base, { ...meta, definitionLookup: undefined })
            : createFromBaseNpcContext(base as unknown as Row, { ...meta, definitionLookup: undefined }, meta.inventoryMetadata!);
        const heldDefinition = definition;
        let finalize: ReturnType<typeof createPlayerDefinitionFinalizer> | undefined;
        let npcFinalize: ReturnType<typeof createNpcDefinitionFinalizer> | undefined;
        definition = id => {
            const held = heldDefinition(id);
            if (record(held?.items) && (record(held?.item_layer_types) || Array.isArray(held?.item_layer_types)) || !readItemUuidSlot(id)) return held;
            const essence = record(meta.resources.essenceShells[id]);
            if (essence) return essence;
            const source = record(raw.definition(id));
            if (source && !Object.keys(record(source.item_layer_types) ?? {}).length) {
                // Empty-layer grants remain embedded instead of catalogued.
                // Finalize only that definition with the entry compiler's own
                // materializer; raw SELECT bytes are never a finished ability.
                if (!player) {
                    npcFinalize ??= createNpcDefinitionFinalizer(base as unknown as Row,
                        { ...meta, definitionLookup: undefined }, meta.inventoryMetadata!);
                    return npcFinalize.definition(id, []);
                }
                finalize ??= createPlayerDefinitionFinalizer(base, { ...meta, definitionLookup: undefined }, 'form-revert-definition');
                return finalize(id, 'ability');
            }
            return source;
        };
        meta.definitionLookup = definition;
        const prefix = player ? constructPlayerPrefixFromBase(base, meta) : { kind: 'constructed-prefix' as const, row: {} as Row };
        if (prefix.kind !== 'constructed-prefix') return fail(prefix.kind === 'missing-input'
            ? `${prefix.stage}:${prefix.dependency}:${prefix.id}` : `${prefix.stage}:${prefix.detail}`);
        // The source constructor also supplies display/save-section data. None
        // enters this mutation's working set or its later cluster compilers.
        const source = prefix.row;
        for (const key of Object.keys(source)) if (key.endsWith('_arr') || ['pictures',
            'encounter_creatures_spells_prepared', '_from_base_providers'].includes(key)) delete source[key];
        const gallery = record((row.encounter_creatures as Row).pictures), sourceCreature = record(source.encounter_creatures);
        if (gallery && sourceCreature) sourceCreature.pictures = gallery;
        let host = source;
        if (player) {
        host = constructPlayerTierSkillsFromBase(base, meta, source);
        host = constructPlayerStatusEnvironmentFromBase(base, meta, host, id => {
            const visit = (rows: Row[]): Row | null => {
                for (const item of rows) {
                    if (upper(readInventoryField(item, 'inventory_id')) === upper(id)) return item;
                    const nested = visit(Array.isArray(item.inventory_in_container) ? item.inventory_in_container : []);
                    if (nested) return nested;
                }
                return null;
            };
            return visit(Array.isArray(row.inventory) ? row.inventory : []);
        });
        }
        countStatusCompileWork('vitality', row);
        const vit = createVitalityCompiler({ ...statusProjectionDependencies(row, definition), ...meta.vitality,
            resolveInventoryIdentity: createInventoryTemplateIdentityResolver(definition), resolveShell: definition })
            .compile({ items: { item_type: player ? 'player_character' : 'creature' }, statuses: player ? host.statuses
                : { target, caster: statusRows(row, 'caster').filter(status => !removed.has(upper(readItemField(status, 'status_id')))) },
                computed: { vitality: { ...vitality, base_v_temp: formRemains
                    ? Number(readItemField(row.encounter_creatures as Row, 'v_temp') ?? 0) : 0 } } }).row as Row;
        const nextVitality = record(record(vit.computed)?.vitality)!;
        if (record(nextVitality.v_status)) nextVitality.v_status = readItemField(nextVitality.v_status as Row, 'item_id');
        if (player) (host.computed as Row).vitality = nextVitality;
        // Thin held inventory has computed vitality/economy on its definition.
        // Retain the native instance fields over those defaults, including qty omission.
        const inventory = (Array.isArray(row.inventory) ? row.inventory : []).map((thin: Row) => {
            const itemId = upper(readInventoryField(thin, 'inventory_item_id'));
            const def = definition(itemId);
            if (!def) throw new Error('inventory-definition-not-held');
            const change = inventoryWrites.find(write => upper(write.inventory_id) === upper(readInventoryField(thin, 'inventory_id')));
            const defaults = record(def.computed), held = record(thin.computed);
            const inputComputed: Row = { vitality: { ...record(defaults?.vitality), ...record(held?.vitality) } };
            for (const key of ['weight', 'cost', 'enchantment', 'weapon', 'armor', 'size', 'light', 'creature', 'possessor']) {
                const value = held?.[key] ?? defaults?.[key];
                if (value !== undefined) inputComputed[key] = value;
            }
            const enchantment = record(inputComputed.enchantment), abilities = record(enchantment?.abilities);
            if (abilities) inputComputed.enchantment = { ...enchantment, abilities: Object.fromEntries(Object.entries(abilities)
                .map(([key, entries]) => [key, (Array.isArray(entries) ? entries as Row[] : []).map(entry => {
                    const id = readAbilityEntryId(entry, { itemField: readItemField, resolveCarrierRef: definition });
                    return { ...entry, item_arr: definition(upper(id)) ?? entry.item_arr };
                })])) };
            // Derived grant carriers such as charged_spell remain at the root
            // after catalog leaning. The source walk must see those too.
            const roots = Object.fromEntries([...new Set([...Object.keys(meta.layerCatalog), ...meta.abilityTypes,
                ...meta.grants.inventoryAbilityTypes])].flatMap(key => {
                const value = thin[key] ?? def[key]; return value === undefined ? [] : [[key, value]];
            }));
            return { ...roots, items: { ...record(def.items), ...record(thin.items) },
                item_layer_types: { ...record(def.item_layer_types), ...record(thin.item_layer_types) },
                ...(thin.inventory_in_container === undefined ? {} : { inventory_in_container: thin.inventory_in_container }),
                ...(thin.catalog ?? def.catalog ? { catalog: thin.catalog ?? def.catalog } : {}),
                computed: inputComputed,
                inventory: { ...record(thin.inventory), ...(change?.kind === 'restore_absorbed' && !replacement.length ? { inventory_private: 0 }
                    : change?.kind === 'unequip_acquired' ? { equipped: '' } : {}) } };
        });
        if (player) {
        host = constructPlayerInventoryGrantsFromBase(base, meta, host, { kind: 'missing-stage', inventory, missingStages: [],
            completedStages: [] }).row;
        const context = createFromBasePlayerStageContext(base, meta, inventory);
        host.total_weight_held = createInventoryTotalsCompiler(context.common).flatCarry(inventory);
        host.gross_weight = Number(context.readers.layerValue(host, 'weight') ?? 0) + Number(host.total_weight_held);
        host = createCreatureSizeRecalculator({ ...context.common, metadata: context.formula, queryFullItem: definition,
            nameFromBlob: (_row, id) => String(readItemField(definition(id), 'item_name') ?? ''),
            resolveItemIdType: id => String(readItemField(definition(id), 'item_type') ?? '') })(host) as Row;
        if (formRemains) {
            // The normal form graft runs on this owned source window. Abilities,
            // castable and known spells still use the shared normal stages below.
            host = createCreatureShapeshiftCompiler({ ...context.common, metadata: meta.shapeshift, queryFullItem: definition,
                applyMonsterTemplate: () => { throw new Error('replacement-template-not-ported'); },
                applyCreatureModelMedia: () => { throw new Error('replacement-model-media-not-ported'); },
                picturesOf: () => { throw new Error('replacement-pictures-not-ported'); } })(host) as Row;
            delete host.derivative_speed_basis;
            delete host.pre_status_speed_layer_presence;
        }
        host = constructPlayerNestedGrantsFromBase(base, meta, host).equipped;
        host = constructPlayerIntegersFromBase(base, meta, host);
        const forced = constructPlayerForcedSkillsFromBase(base, meta, host);
        host = constructPlayerFullSkillEntriesFromBase(base, meta, forced.row, forced.forced).row;
        host = constructPlayerFeaturesFromBase(base, meta, host);
        host = constructPlayerDefenseGrantsFromBase(base, meta, constructPlayerDefenseSeedsFromBase(base, meta, host));
        host = constructPlayerArmorClassFromBase(base, meta, host);
        host = constructPlayerResourcesFromBase(base, meta, host);
        host = constructActorAbilitiesFromBase(base, meta, host, createFromBasePlayerGrantContext(base, meta, host), base);
        host = createCreatureEquippedCountsCompiler({ ...createFromBasePlayerGrantContext(base, meta, host).deps,
            ...meta.equippedCounts })(host) as Row;
        host = constructPlayerPostFinalizerFromBase(base, meta, host);
        host = constructPlayerClustersFromBase(base, meta, host, { prepared: false }).row;
        constructFromBaseReachWatch(host, context.readers, definition);
        let rebuilt = record(record(host.computed)?.creature)!;
        constructPlayerAbilityClustersFromBase(base, meta, host);
        const combat = { ...record(prior.combat) };
        for (const key of ['extra_attack', 'hindered_attack_counter']) delete combat[key];
        const value = createFromBasePlayerGrantContext(base, meta, host).readers.layerValue;
        rebuilt.combat = { ...combat, ...record(rebuilt.combat), main_weapon_damage_type: compileCreatureMainWeaponDamageType({
            mainHandWeaponCount: Number(value(host, 'count_main_hand_weapon_equipped') ?? 0),
            offHandWeaponCount: Number(value(host, 'count_off_hand_weapon_equipped') ?? 0),
            bothHandsWeaponCount: Number(value(host, 'count_both_hands_weapon_equipped') ?? 0),
            inventoryRows: inventory.map(item => ({ equippedSlot: String(readInventoryField(item, 'equipped') ?? ''),
                itemType: String(readItemField(item, 'item_type') ?? ''),
                damageType: value(item, 'damage_type') == null ? null : String(value(item, 'damage_type')) })) }) };
        relocateCreatureDerivedToComputed(host, context.readers);
        // Match the post-relocation spell view: root grant buckets are already
        // represented by the finalized computed abilities at the native bake.
        constructPlayerSpellClustersFromBase(base, meta, Object.fromEntries(['computed', 'inventory',
            'player_characters', 'encounter_creatures', 'spell_prepared_1', 'spell_prepared_2', 'spell_prepared_3']
            .filter(key => key in host).map(key => [key, host[key]])));
        } else {
            countStatusCompileWork('speeds', row);
            host = constructNpcFormRemovalWindow(base as unknown as Row, meta, inventory, definition, initialNpc || survivingForm);
            (host.computed as Row).vitality = nextVitality;
        }
        const context = player ? createFromBasePlayerStageContext(base, meta, inventory)
            : createFromBaseNpcContext(base as unknown as Row, meta, meta.inventoryMetadata!);
        let rebuilt = record(record(host.computed)?.creature)!;
        // Apply the initial-load cluster normalization too (UUID shells and
        // omitted empty contributor lists), without walking the held member.
        const fragment = normalizePlayerClusterValues({ computed: { creature: rebuilt } }, context.readers, id => {
            const def = definition(id);
            return def ? { items: Object.fromEntries(['item_id', 'item_name', 'item_type'].map(key => [key, readItemField(def, key)])) } : null;
        }) as Row;
        // Thin charge defaults against the same held definitions as entry compile.
        // Seed only this ability window; the member's catalog stays untouched.
        const heldCatalog: Row = {}, catalog = record(row.catalog);
        for (const entries of Object.values(record(rebuilt.abilities) ?? {})) if (Array.isArray(entries)) for (const entry of entries) {
            const id = readAbilityEntryId(entry, { itemField: readItemField, resolveCarrierRef: definition });
            const held = catalog?.[id] ?? catalog?.[id.toUpperCase()] ?? catalog?.[id.toLowerCase()];
            if (record(held)) heldCatalog[id] = held;
        }
        const lean = normalizeFromBaseCatalog(fragment, {
            definition, rawDefinition: id => (base.catalog as Record<string, Row>)[id] ?? null, abilityTypes: meta.abilityTypes, heldCatalog });
        rebuilt = record(record(lean.computed)?.creature)!;
        const creature = { ...prior };
        for (const key of CLUSTERS) {
            if (key in rebuilt) creature[key] = diffFormCluster(prior[key], rebuilt[key]); else delete creature[key];
        }
        // Resources own essence; the native spell view restores castable limits.
        // Only the pre-fold arcane threshold needs the held/restored family below.
        const casting = { ...record(prior.spellcasting), ...record(rebuilt.spellcasting) };
        for (const key of ['essence', 'essence_mod']) {
            const value = readItemField(record(rebuilt.spellcasting), key);
            if (value == null) delete casting[key];
            else casting[key] = key === 'essence' ? readItemUuidSlot(value) : value;
        }
        for (const cluster of ['modifiers', 'modifier_sources']) {
            const values = { ...record(prior[cluster]) };
            for (const key of changedLayers) {
                const next = record(rebuilt[cluster]);
                if (next && key in next) values[key] = next[key]; else delete values[key];
            }
            if (Object.keys(values).length) creature[cluster] = diffFormCluster(prior[cluster], values); else delete creature[cluster];
        }
        if (changedLayers.has('arcane_threshold')) {
            // This window stops before C-PI. Mirror its offset/brace rule over
            // the restored sources instead of importing the pre-fold default.
            const total = phpJsonInt(readItemField(record(creature.modifiers), 'arcane_threshold'));
            const sources = context.readers.layerEntries(record(creature.modifier_sources) ?? {}, 'arcane_threshold');
            const braced = Object.values(sources).some(source =>
                readItemField(record(source), 'value_brace') !== null && !phpJsonTruthy(readItemField(record(source), 'suppressed')));
            const offset = CPI_FAMILY_OFFSET.arcane_threshold!;
            casting.arcane_threshold = (braced || total === 0 ? total : offset + total) || offset;
        } else if ('arcane_threshold' in (record(prior.spellcasting) ?? {})) {
            casting.arcane_threshold = readItemField(record(prior.spellcasting), 'arcane_threshold');
        } else delete casting.arcane_threshold;
        creature.spellcasting = diffFormCluster(prior.spellcasting, casting);
        creature.meta = diffFormCluster(prior.meta, { ...record(prior.meta), max_summons: record(rebuilt.meta)?.max_summons });
        for (const key of FORM_KEYS) {
            if (key in rebuilt) creature[key] = diffFormCluster(prior[key], rebuilt[key]); else delete creature[key];
        }
        const rootContext = player ? createFromBasePlayerGrantContext(base, meta, host)
            : createFromBaseNpcGrantContext(base as unknown as Row, meta, host);
        const conditions = createAbilityConditionEvaluator(rootContext.deps);
        const rootFields = compileStatusRootFields(row, host, { definition,
            entries: (r, key) => Object.values(rootContext.readers.layerEntries(r, key)), value: rootContext.readers.layerValue,
            active: (def, r) => conditions.check(def, r, false, false), buckets: r => record(record(record(r.computed)?.creature)?.abilities) ?? {},
            gearLayers: meta.grants.redundancyCandidates, quantityIds: meta.rollup.quantityIds,
            // The shared root walker already excludes the three AC/DR layers
            // counted on equipped rows; other layers retain their root shares.
            gearCounted: () => false });
        return { ok: true, creature, vitality: nextVitality,
            rootScalars: { shapeshifted: formRemains ? 1 : null }, rootFields, catalog: record(row.catalog),
            addIds: added.map(status => status.status_id) };
    } catch (error) { return fail(String(error instanceof Error ? error.message : error)); } });
}
